import { useCallback, useEffect, useRef, useState } from "react";
import {
  addDBRouteDcRoutingPacking,
  deleteDBRouteDcRoutingPacking,
  updateDBRouteDcRoutingPacking,
} from "@services/api";
import { USE_FIXTURES } from "../constants";
import {
  addDBRouteFixture,
  deleteDBRouteFixture,
  updateDBRouteFixture,
} from "../__fixtures__/fixtureStore";
import { notifySuccess, notifyError } from "@components/ds/notify";

// Wraps the dc-routing-packing-namespace route binding helpers
// (POST/PUT/DELETE /dc-routing-packing/v1/dbs/:db_id/routes[/:route_id]).
// DBSettingsSheet + RoutePickerSheet call into this so route management
// for a single DB happens inline without leaving the screen. The legacy
// addDBRoute/updateDBRoute/deleteDBRoute helpers in services/api.js stay
// for the older CpDeliveryRoutes screen.

const useDbRoutesEdit = ({ reload } = {}) => {
  const [submitting, setSubmitting] = useState(false);
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const wrap = useCallback(
    async (fn, successMsg, errMsg) => {
      setSubmitting(true);
      try {
        await fn();
        notifySuccess(successMsg);
        if (reload) await reload();
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          errMsg;
        notifyError(msg);
        return false;
      } finally {
        if (isMounted.current) setSubmitting(false);
      }
    },
    [reload],
  );

  const addRoute = useCallback(
    ({ db_id, route_id, route_name }) =>
      wrap(
        async () => {
          if (USE_FIXTURES) {
            addDBRouteFixture({ db_id, route_id, route_name });
            return;
          }
          await addDBRouteDcRoutingPacking({ db_id, route_id, route_name });
        },
        `Route ${route_name} added`,
        "Failed to add route",
      ),
    [wrap],
  );

  const removeRoute = useCallback(
    ({ db_id, route_id }) =>
      wrap(
        async () => {
          if (USE_FIXTURES) {
            deleteDBRouteFixture({ db_id, route_id });
            return;
          }
          await deleteDBRouteDcRoutingPacking({ db_id, route_id });
        },
        "Route removed",
        "Failed to remove route",
      ),
    [wrap],
  );

  const renameRoute = useCallback(
    ({ db_id, route_id, route_name }) =>
      wrap(
        async () => {
          if (USE_FIXTURES) {
            updateDBRouteFixture({ db_id, route_id, route_name });
            return;
          }
          await updateDBRouteDcRoutingPacking({ db_id, route_id, route_name });
        },
        "Route updated",
        "Failed to update route",
      ),
    [wrap],
  );

  // Batch route changes for one DB — used by the new RoutePickerSheet.
  // Fans add/remove out in parallel; refetches once at the end. Reports
  // a single success / partial-failure toast so the user doesn't see a
  // toast storm.
  const saveRoutes = useCallback(
    async ({ db_id, toAdd = [], toRemove = [] }) => {
      if (!db_id || (toAdd.length === 0 && toRemove.length === 0)) return true;
      setSubmitting(true);
      try {
        const adds = toAdd.map((r) => {
          if (USE_FIXTURES) {
            addDBRouteFixture({
              db_id,
              route_id: r.route_id,
              route_name: r.route_name,
            });
            return Promise.resolve();
          }
          return addDBRouteDcRoutingPacking({
            db_id,
            route_id: r.route_id,
            route_name: r.route_name,
          }).catch((e) => ({ failed: true, route: r, error: e }));
        });
        const removes = toRemove.map((r) => {
          if (USE_FIXTURES) {
            deleteDBRouteFixture({ db_id, route_id: r.route_id });
            return Promise.resolve();
          }
          return deleteDBRouteDcRoutingPacking({
            db_id,
            route_id: r.route_id,
          }).catch((e) => ({
            failed: true,
            route: r,
            error: e,
          }));
        });
        const results = await Promise.all([...adds, ...removes]);
        const failed = results.filter((res) => res?.failed);
        if (failed.length === 0) {
          notifySuccess(
            `Routes updated · +${toAdd.length} / −${toRemove.length}`,
          );
        } else {
          notifyError(`Failed on ${failed.length} of ${results.length} routes`);
        }
        if (reload) await reload();
        return failed.length === 0;
      } catch (e) {
        notifyError(e?.message || "Failed to update routes");
        return false;
      } finally {
        if (isMounted.current) setSubmitting(false);
      }
    },
    [reload],
  );

  return { addRoute, removeRoute, renameRoute, saveRoutes, submitting };
};

export default useDbRoutesEdit;
